import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './JoinServicesModal.module.scss';
import { Button } from '@mui/material';
import clsx from 'clsx';
import useWindowSize from 'utils/useWindowSize';
import { salonManagementActions } from 'Containers/SalonManagement/store';
import SelectorMenu from 'Containers/Common/CustomSelector/SelectorMenu';
import { getServices } from 'Containers/SalonManagement/store/selectors';
import { ReactComponent as AlertIcon } from 'assets/svg/icons/alert.svg';

const normalizeId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

export default ({ closeModal, activeServices, worker }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const services = useSelector(getServices());

  const formedServices = useMemo(
    () =>
      services
        .filter((service) => service.disabilitato == 0)
        .map((service) => ({
          name: service.name,
          value: normalizeId(service.id),
          icon: service.icon,
          cost: service.prezzo,
          duration: service.time,
          onlyVisibile: service.onlyVisibile,
        })),
    [services],
  );

  const initialIds = useMemo(
    () => [...(activeServices || worker?.activeServicesIds || [])].map(normalizeId),
    [activeServices, worker?.activeServicesIds],
  );

  // Preseleziona i servizi già assegnati: icona meno e si possono togliere
  const [selectorValues, setSelectorValues] = useState(initialIds);

  useEffect(() => {
    setSelectorValues(initialIds);
  }, [initialIds]);

  const hasChanges = useMemo(() => {
    if (selectorValues.length !== initialIds.length) return true;
    const initialSet = new Set(initialIds);
    return selectorValues.some((id) => !initialSet.has(id));
  }, [selectorValues, initialIds]);

  const saveValue = () => {
    if (!hasChanges) return;

    const currentIds = (worker.activeServicesIds || []).map(normalizeId);
    const sendServices = selectorValues.map(normalizeId);
    const activeServicesIds = sendServices.filter((id) => !currentIds.includes(id));
    const servicesIdsToDelete = currentIds.filter((id) => !sendServices.includes(id));

    if (!activeServicesIds.length && !servicesIdsToDelete.length) {
      closeModal();
      return;
    }

    const workerId = worker.idpoltrona || worker.id;
    dispatch(
      salonManagementActions.EDIT_WORKER_SERVICES.REQUEST(
        {
          idPoltrona: worker.idpoltrona,
          activeServicesIds,
          servicesIdsToDelete,
        },
        () => {
          if (workerId) {
            dispatch(
              salonManagementActions.GET_WORKER.REQUEST(workerId, null, { skipLoader: true }),
            );
            dispatch(
              salonManagementActions.GET_WORKER_SERVICES.REQUEST(workerId, null, {
                skipLoader: true,
              }),
            );
          }
          closeModal();
        },
      ),
    );
  };

  return (
    <div
      className={clsx(styles.service_modal_wrapper, {
        [styles.service_modal_wrapper_mobile]: isMobile,
      })}
    >
      <div className={styles.body}>
        {!formedServices.length ? (
          <p className={styles.alert}>
            <AlertIcon />
            {t('workers.serviceList.joinServicesModal.noServicesToAdd')}
          </p>
        ) : null}
        <SelectorMenu
          isMultiple={true}
          value={selectorValues}
          options={formedServices}
          setIsDropdownOpen={() => {}}
          setValue={setSelectorValues}
          fullHeight={true}
          border={false}
        />
      </div>
      <hr className={styles.divider} />
      <div className={clsx(styles.actions_container, styles.footer)}>
        <Button
          onClick={saveValue}
          className={clsx(styles.confirm_button, {
            [styles.button_disabled]: !hasChanges,
          })}
          variant="contained"
          type="button"
          disabled={!hasChanges}
        >
          {t('workers.serviceList.joinServicesModal.save')}
        </Button>
      </div>
    </div>
  );
};
